import { defineStore } from 'pinia'
import api from '@/services/api'

export interface GridLayout { x: number; y: number; w: number; h: number }

export interface WidgetStyle {
  showHeader?: boolean
  displayTitle?: string
  backgroundColor?: string
  template?: string
}

export interface AccessRule {
  principalType: 'ALL' | 'ROLE' | 'USER' | 'ACCOUNT'
  principalValueList?: string[]
  bonoApplication?: string
}

export interface Widget {
  id: string
  name: string
  type: string
  config: Record<string, any>
  style?: WidgetStyle
}

export interface DashboardWidget {
  id: string
  widgetId: string
  widget: Widget
  layout: GridLayout
  isShared: boolean
}

export interface PublishedSlot {
  slotKey: string
  paramMapping?: Record<string, string>
}

export interface Dashboard {
  id: string
  name: string
  description?: string
  createdBy?: string
  copiedFromId?: string
  sharingMode: 'PRIVATE' | 'TEAM' | 'SPECIFIC'
  sharedWithUserIds?: string[]
  accessRuleList?: AccessRule[]
  widgets: DashboardWidget[]
  createdAt: string
  updatedAt: string
  publishedSlots?: PublishedSlot[]
}

export interface WidgetData {
  rows: any[]
  singleValue?: boolean
  executionTimeMs?: number
  fetchedAt: string
}

export interface LayoutSavePayload {
  widgets: Array<{ id: string; layout: GridLayout }>
}

export interface PublishSlotPayload {
  slotKey: string
  paramMapping?: Record<string, string>
  accessRules: AccessRule[]
}

export interface PublishPayload {
  slots: PublishSlotPayload[]
  propagateToWidgets?: boolean
}

/** Consume-side response for a page slot: dashboard + its param mapping. */
export interface SlotDashboard {
  dashboard: Dashboard
  paramMapping?: Record<string, string>
}

export const useDashboardStore = defineStore('sc-dashboard', {
  state: () => ({
    // ── CRUD (user-owned dashboards) ──────────────────────────────────────────
    dashboards: [] as Dashboard[],
    activeDashboard: null as Dashboard | null,
    loading: false,
    saving: false,

    // ── Per-widget data keyed by DashboardWidget.id ───────────────────────────
    widgetData: {} as Record<string, WidgetData>,
    widgetLoading: {} as Record<string, boolean>,
    widgetErrors: {} as Record<string, string>,
  }),

  actions: {
    // ── Dashboard CRUD ────────────────────────────────────────────────────────

    async fetchDashboards() {
      this.loading = true
      try {
        const { data } = await api.get<Dashboard[]>('/dashboard/dashboards')
        this.dashboards = data
      } catch (err) {
        console.error('fetchDashboards:', err)
      } finally {
        this.loading = false
      }
    },

    async fetchDashboard(id: string) {
      this.loading = true
      try {
        const { data } = await api.get<Dashboard>(`/dashboard/dashboards/${id}`)
        this.activeDashboard = data
        return data
      } catch (err) {
        console.error('fetchDashboard:', err)
        return null
      } finally {
        this.loading = false
      }
    },

    async createDashboard(payload: { name: string; sharingMode?: string; accessRuleList?: AccessRule[] }) {
      try {
        const { data } = await api.post<Dashboard>('/dashboard/dashboards', {
          name: payload.name,
          sharingMode: payload.sharingMode ?? 'PRIVATE',
          accessRuleList: payload.accessRuleList ?? [],
        })
        this.dashboards.push(data)
        return data
      } catch (err) {
        console.error('createDashboard:', err)
        return null
      }
    },

    async updateDashboard(id: string, patch: Partial<Dashboard>) {
      this.saving = true
      try {
        const { data } = await api.put<Dashboard>(`/dashboard/dashboards/${id}`, patch)
        const idx = this.dashboards.findIndex(d => d.id === id)
        if (idx !== -1) this.dashboards[idx] = data
        if (this.activeDashboard?.id === id) this.activeDashboard = data
        return data
      } catch (err) {
        console.error('updateDashboard:', err)
        return null
      } finally {
        this.saving = false
      }
    },

    async deleteDashboard(id: string) {
      try {
        await api.delete(`/dashboard/dashboards/${id}`)
        this.dashboards = this.dashboards.filter(d => d.id !== id)
        if (this.activeDashboard?.id === id) this.activeDashboard = null
      } catch (err) {
        console.error('deleteDashboard:', err)
      }
    },

    async copyDashboard(id: string) {
      try {
        const { data } = await api.post<Dashboard>(`/dashboard/dashboards/${id}/copy`)
        this.dashboards.push(data)
        return data
      } catch (err) {
        console.error('copyDashboard:', err)
        return null
      }
    },

    async saveLayout(id: string, payload: LayoutSavePayload) {
      this.saving = true
      try {
        await api.put(`/dashboard/dashboards/${id}/layout`, payload)
        // Sync activeDashboard layouts so DashboardView renders correct positions immediately
        if (this.activeDashboard?.id === id) {
          const layoutMap = Object.fromEntries(payload.widgets.map(w => [w.id, w.layout]))
          this.activeDashboard = {
            ...this.activeDashboard,
            widgets: this.activeDashboard.widgets.map(dw => ({
              ...dw,
              layout: layoutMap[dw.id] ?? dw.layout,
            })),
          }
        }
      } catch (err) {
        console.error('saveLayout:', err)
      } finally {
        this.saving = false
      }
    },

    async addWidget(dashboardId: string, widgetId: string) {
      try {
        const { data } = await api.post<Dashboard>(
          `/dashboard/dashboards/${dashboardId}/widgets`,
          { widgetId }
        )
        if (this.activeDashboard?.id === dashboardId) {
          this.activeDashboard = data
        }
        return data
      } catch (err) {
        console.error('addWidget:', err)
        return null
      }
    },

    async removeWidget(dashboardId: string, dwId: string) {
      try {
        await api.delete(`/dashboard/dashboards/${dashboardId}/widgets/${dwId}`)
        if (this.activeDashboard?.id === dashboardId) {
          this.activeDashboard = {
            ...this.activeDashboard,
            widgets: this.activeDashboard.widgets.filter(w => w.id !== dwId),
          }
        }
      } catch (err) {
        console.error('removeWidget:', err)
      }
    },

    // ── Publish (slot + access rules) ─────────────────────────────────────────

    async fetchCurrentSlots(id: string): Promise<PublishedSlot[]> {
      try {
        const { data } = await api.get<PublishedSlot[]>(`/dashboard/dashboards/${id}/slots`)
        return data ?? []
      } catch (err) {
        console.error('fetchCurrentSlots:', err)
        return []
      }
    },

    async publishDashboard(id: string, payload: PublishPayload): Promise<PublishedSlot[] | null> {
      this.saving = true
      try {
        const { data } = await api.post<PublishedSlot[]>(`/dashboard/dashboards/${id}/publish`, payload)
        return data ?? []
      } catch (err) {
        console.error('publishDashboard:', err)
        return null
      } finally {
        this.saving = false
      }
    },

    async unpublishAll(id: string): Promise<boolean> {
      this.saving = true
      try {
        await api.delete(`/dashboard/dashboards/${id}/publish`)
        return true
      } catch (err) {
        console.error('unpublishAll:', err)
        return false
      } finally {
        this.saving = false
      }
    },

    /**
     * Consume side: dashboards published to a page slot that the current user may see.
     * Access is resolved entirely on the backend — render the list directly, no filtering.
     */
    async fetchSlotDashboards(slotKey: string): Promise<SlotDashboard[]> {
      try {
        const { data } = await api.get<SlotDashboard[]>(`/dashboard/dashboards/slot/${slotKey}`)
        return data ?? []
      } catch (err) {
        console.error('fetchSlotDashboards:', err)
        return []
      }
    },

    // ── Widget data ───────────────────────────────────────────────────────────

    async fetchWidgetData(dwId: string, widgetId: string, params?: Record<string, string>) {
      this.widgetLoading = { ...this.widgetLoading, [dwId]: true }
      delete this.widgetErrors[dwId]
      try {
        const { data } = await api.post<{ rows: any[]; singleValue?: boolean; executionTimeMs?: number }>(
          `/dashboard/widgets/${widgetId}/execute`,
          params ?? {}
        )
        this.widgetData = {
          ...this.widgetData,
          [dwId]: { ...data, fetchedAt: new Date().toISOString() },
        }
      } catch (err: any) {
        const msg = err?.response?.data?.message ?? 'Execution failed'
        this.widgetErrors = { ...this.widgetErrors, [dwId]: msg }
      } finally {
        this.widgetLoading = { ...this.widgetLoading, [dwId]: false }
      }
    },

    clearWidgetData(dwId?: string) {
      if (dwId) {
        delete this.widgetData[dwId]
        delete this.widgetLoading[dwId]
        delete this.widgetErrors[dwId]
      } else {
        this.widgetData = {}
        this.widgetLoading = {}
        this.widgetErrors = {}
      }
    },
  },
})